import { useCallback, useMemo, useState } from 'react';
import { Button } from '@framework/ui/atoms/Button';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { useGroupMemberUserIds } from '@group/hooks';
import type { WorkspaceInternalPublicMemberRoleJSON, WorkspaceJSON } from 'balus-common';
import type { Workspace } from '@workspace/domain/workspace';
import { trpcClient } from '@framework/trpcClient';
import { difference } from 'lodash-es';
import { useWorkspaceMemberRoles } from '@workspace/domain/workspace/useWorkspaceMemberRoles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { useGroupLicensePermissions } from '@group/License/useGroupLicensePermissions';
import { Tooltip } from '@framework/ui/atoms/Tooltip';
import { useTranslation, Trans } from 'react-i18next';

export type InternalPublicSetting = WorkspaceInternalPublicMemberRoleJSON | null;

type SettingState = WorkspaceJSON['setting'] & { internalPublicSetting: InternalPublicSetting };

type Props = {
  onClose(): void;
  workspace: Workspace;
  internalPublicSetting: InternalPublicSetting;
  groupEnabledIndividualViewModelPermissions: boolean;
};

export const ShareSettingEditModal: React.FC<Props> = ({
  onClose,
  workspace,
  internalPublicSetting: initialInternalPublicSetting,
  groupEnabledIndividualViewModelPermissions,
}: Props) => {
  const { t } = useTranslation();
  const internalPublicSettingOptions: Record<WorkspaceInternalPublicMemberRoleJSON, string> = useMemo(
    () => ({
      editor: t('workspace.shareSettingEdit.roleEditor'),
      viewer: t('workspace.shareSettingEdit.roleViewer'),
    }),
    [t]
  );
  const groupMemberUserIds = useGroupMemberUserIds(workspace.groupId, { excludeGuest: true });
  const workspaceUserIds = useWorkspaceMemberRoles(workspace.id)?.userIds();
  const addingMemberIds =
    groupMemberUserIds && workspaceUserIds ? difference(groupMemberUserIds, workspaceUserIds) : [];
  const groupLicensePermissions = useGroupLicensePermissions();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [state, setState] = useState<SettingState>({
    ...workspace.setting.dump(),
    internalPublicSetting: initialInternalPublicSetting,
  });

  // グループ内公開設定
  const handlePrivateWorkspace = useCallback(() => {
    setState((prev) => ({
      ...prev,
      internalPublicSetting: null,
    }));
  }, []);
  const handleInternalPublicWorkspace = useCallback(() => {
    setState((prev) => ({
      ...prev,
      internalPublicSetting: 'editor',
    }));
  }, []);
  const handleChangeInternalSetting = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      if (value in internalPublicSettingOptions) {
        setState((prev) => ({
          ...prev,
          internalPublicSetting: value as InternalPublicSetting,
        }));
      }
    },
    [internalPublicSettingOptions]
  );

  // ビューモデルごとの権限設定
  const handleChangeIndividualViewModelPermissions = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({
      ...prev,
      enableIndividualViewModelPermissions: event.target.checked,
    }));
  }, []);

  // 外部公開設定
  const handleChangePublicSpace = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({
      ...prev,
      isPublicSpace: event.target.checked,
    }));
  }, []);
  const handleChangeViewModelURLShareable = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({
      ...prev,
      isViewModelURLShareable: event.target.checked,
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      await trpcClient.workspace.updateWorkspaceShareSetting.mutate({
        workspaceId: workspace.id,
        isPublicSpace: state.isPublicSpace,
        isViewModelURLShareable: state.isViewModelURLShareable,
        enableIndividualViewModelPermissions: state.enableIndividualViewModelPermissions,
        internalPublicSetting: state.internalPublicSetting,
      });
      onClose();
    } catch (e) {
      setErrorMessage(t('workspace.edit.updateError'));
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  }, [onClose, state, workspace.id, t]);

  return (
    <GeneralModal
      isOpen
      onClose={onClose}
      submitButton={
        <Button color="primary" onClick={handleSubmit} loading={isSubmitting}>
          {t('common.action.save')}
        </Button>
      }
      cancelButton={
        <Button color="secondary" onClick={onClose} disabled={isSubmitting}>
          {t('common.action.cancel')}
        </Button>
      }
      title={t('workspace.shareSettingEdit.title')}
    >
      <div className="overflow-y-auto p-4" style={{ width: '600px', maxHeight: '70vh' }}>
        <div className="py-2 font-bold">{t('workspace.shareSettingEdit.internalPublicSection')}</div>

        <div className="py-2">
          <label className="block cursor-pointer">
            <input
              type="radio"
              name="internal-setting"
              className="form-radio mr-1 size-6 border-brand"
              checked={state.internalPublicSetting === null}
              onChange={handlePrivateWorkspace}
              disabled={isSubmitting}
            />
            {t('workspace.shareSettingEdit.privateOption')}
          </label>
          <span className="ml-8 py-1 text-sm">{t('common.label.privateWorkspaceDescription')}</span>

          {initialInternalPublicSetting !== null && state.internalPublicSetting === null && (
            <div className="ml-8 mt-2 rounded border border-yellow-700 bg-yellow-200 p-2 text-sm">
              <Trans i18nKey="workspace.shareSettingEdit.privateChangedWarning" components={{ lineBreak: <br /> }} />
            </div>
          )}
        </div>

        <div className="py-2">
          <label className="block cursor-pointer">
            <input
              type="radio"
              name="internal-setting"
              className="form-radio mr-1 size-6 border-brand"
              checked={state.internalPublicSetting !== null}
              onChange={handleInternalPublicWorkspace}
              disabled={isSubmitting}
            />
            {t('common.label.internalPublicWorkspace')}
          </label>
          <span className="ml-8 py-1 text-sm">{t('common.label.internalPublicWorkspaceDescription')}</span>

          <div className="ml-8">
            <select
              className="form-select block w-64 py-1"
              value={state.internalPublicSetting || ''}
              onChange={handleChangeInternalSetting}
              disabled={isSubmitting || state.internalPublicSetting == null}
            >
              {Object.entries(internalPublicSettingOptions).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {state.internalPublicSetting !== null && addingMemberIds.length > 0 && (
            <div className="ml-8 mt-2 rounded border border-yellow-700 bg-yellow-200 p-2 text-sm">
              <Trans
                i18nKey="workspace.shareSettingEdit.addingMembersNotice"
                values={{ count: addingMemberIds.length }}
                components={{ countSpan: <b className="px-1" /> }}
              />
            </div>
          )}

          {initialInternalPublicSetting !== null &&
            state.internalPublicSetting !== null &&
            initialInternalPublicSetting != state.internalPublicSetting && (
              <div className="ml-8 mt-2 rounded border border-yellow-700 bg-yellow-200 p-2 text-sm">
                <Trans
                  i18nKey="workspace.shareSettingEdit.permissionChangedWarning"
                  components={{ lineBreak: <br /> }}
                />
              </div>
            )}
        </div>

        <div className="py-2 font-bold">{t('workspace.individualPermission.sectionTitle')}</div>

        {groupEnabledIndividualViewModelPermissions ? (
          <>
            <div className="py-2">
              <Trans
                i18nKey="workspace.shareSettingEdit.individualPermEnabledNote"
                components={{
                  helpLink: (
                    <a
                      href="https://balus-help.levii.co.jp/hc/ja/articles/48249939676825"
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-700 underline"
                    >
                      <FontAwesomeIcon icon={faExternalLink} className="pl-1" />
                    </a>
                  ),
                }}
              />
              <br />
              {t('workspace.shareSettingEdit.individualPermToggleNote')}
            </div>

            <div className="py-2">
              <label className="block cursor-pointer">
                <input
                  type="checkbox"
                  className="form-checkbox mr-1 size-6 border-brand"
                  checked={state.enableIndividualViewModelPermissions}
                  onChange={handleChangeIndividualViewModelPermissions}
                  disabled={isSubmitting}
                />
                {t('workspace.shareSettingEdit.individualPermCheckbox')}
              </label>
            </div>
          </>
        ) : (
          <div className="py-2">
            <Trans
              i18nKey="workspace.shareSettingEdit.individualPermDisabledNote"
              components={{
                helpLink: (
                  <a
                    href="https://balus-help.levii.co.jp/hc/ja/articles/48249939676825"
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-700 underline"
                  >
                    <FontAwesomeIcon icon={faExternalLink} className="pl-1" />
                  </a>
                ),
              }}
            />
          </div>
        )}

        <div className="py-2 font-bold">{t('workspace.shareSettingEdit.publicSection')}</div>

        <div className="py-2">{t('workspace.shareSettingEdit.publicSectionDesc')}</div>

        <div className="py-2">
          {groupLicensePermissions?.publicSpace ? (
            <label className="block cursor-pointer">
              <input
                type="checkbox"
                className="form-checkbox mr-1 size-6 border-brand"
                checked={state.isPublicSpace}
                onChange={handleChangePublicSpace}
                disabled={isSubmitting}
              />
              {t('workspace.shareSettingEdit.publicSpaceCheckbox')}
            </label>
          ) : (
            <Tooltip tooltip={t('workspace.shareSettingEdit.publicLicenseTooltip')} placement="top">
              <label className="block cursor-not-allowed text-gray-400">
                <input
                  type="checkbox"
                  className="form-checkbox mr-1 size-6 cursor-not-allowed border-gray-500 bg-gray-400"
                  checked={state.isPublicSpace}
                  disabled
                />
                {t('workspace.shareSettingEdit.publicSpaceCheckbox')}
              </label>
            </Tooltip>
          )}

          {!workspace.setting.isPublicSpace && state.isPublicSpace && (
            <div className="ml-8 mt-2 rounded border border-yellow-700 bg-yellow-200 p-2 text-sm">
              <Trans
                i18nKey="workspace.shareSettingEdit.publicSpaceChangedWarning"
                components={{ lineBreak: <br /> }}
              />
            </div>
          )}
        </div>

        <div className="py-2">
          {groupLicensePermissions?.viewModelURLShare ? (
            <label className="block cursor-pointer">
              <input
                type="checkbox"
                className="form-checkbox mr-1 size-6 border-brand"
                checked={state.isViewModelURLShareable}
                onChange={handleChangeViewModelURLShareable}
                disabled={isSubmitting}
              />
              {t('workspace.shareSettingEdit.urlShareableCheckbox')}
            </label>
          ) : (
            <Tooltip tooltip={t('workspace.shareSettingEdit.urlShareableLicenseTooltip')} placement="top">
              <label className="block cursor-not-allowed text-gray-400">
                <input
                  type="checkbox"
                  className="form-checkbox mr-1 size-6 cursor-not-allowed border-gray-500 bg-gray-400"
                  checked={state.isViewModelURLShareable}
                  disabled
                />
                {t('workspace.shareSettingEdit.urlShareableCheckbox')}
              </label>
            </Tooltip>
          )}
        </div>

        {errorMessage && <div className="py-2 text-red-500">{errorMessage}</div>}
      </div>
    </GeneralModal>
  );
};
